import React, { useEffect, useMemo, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Alert, Button, Card, Col, Form, Input, message, Modal, Popconfirm, Row, Select, Space, Table, Tag, Typography } from 'antd';
import { ArrowLeftOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import {
  createDispensesCoursBatch,
  deleteDispenseCours,
  getDispenseModalClasseData,
  getDispensesCours,
  getDispensesCoursGroup,
  resetGroup,
  resetModalData,
} from './dispense-cours.reducer';
import { buildDispenseInscriptionOptions, formatDispenseMatiereLabel, groupDispensesCours } from './dispenses-cours.utils';
import { IDispenseCours } from 'app/shared/model/dispense-cours.model';

const { Title, Text } = Typography;

export const DispensesCoursDetail = (
  props: RouteComponentProps<{ ecoleid: string; anneeScolaireId: string; classeId: string; matiereId: string }>
) => {
  const dispatch = useAppDispatch();
  const { ecoleid, anneeScolaireId, classeId, matiereId } = props.match.params;
  const sousMatiereId = new URLSearchParams(props.location.search).get('sousMatiereId') || undefined;
  const dispenseCoursState = useAppSelector(state => state.dispenseCours);
  const { groupEntities, groupLoading, modalEleves, entities, updating, modalLoading } = dispenseCoursState;
  const [search, setSearch] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');
  const [form] = Form.useForm();

  const loadGroup = () => {
    dispatch(getDispensesCoursGroup({ ecoleId: ecoleid, anneeScolaireId, classeId, matiereId, sousMatiereId }));
  };

  useEffect(() => {
    dispatch(resetGroup());
    loadGroup();
    dispatch(getDispensesCours({ ecoleId: ecoleid, anneeScolaireId }));
    dispatch(getDispenseModalClasseData({ ecoleId: ecoleid, anneeScolaireId, classeId }));
    return () => {
      dispatch(resetGroup());
      dispatch(resetModalData());
    };
  }, [ecoleid, anneeScolaireId, classeId, matiereId, sousMatiereId]);

  const group = useMemo(() => groupDispensesCours(groupEntities)[0], [groupEntities]);

  const filteredDispenses = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return groupEntities;
    return groupEntities.filter(dispense =>
      `${dispense.elevePrenom || ''} ${dispense.eleveNom || ''} ${dispense.matricule || ''}`.toLowerCase().includes(needle)
    );
  }, [groupEntities, search]);

  const inscriptionOptions = useMemo(
    () =>
      buildDispenseInscriptionOptions(modalEleves, entities, {
        classeId,
        matiereId,
        sousMatiereId,
        search: studentSearch,
      }),
    [modalEleves, entities, classeId, matiereId, sousMatiereId, studentSearch]
  );

  const goBack = () => props.history.push(`/eleve/${ecoleid}/${anneeScolaireId}/dispenses`);

  const deleteDispense = async (dispense: IDispenseCours) => {
    await dispatch(deleteDispenseCours(dispense.id)).unwrap();
    message.success('Dispense supprimée');
    if (groupEntities.length <= 1) {
      goBack();
      return;
    }
    loadGroup();
    dispatch(getDispensesCours({ ecoleId: ecoleid, anneeScolaireId }));
  };

  const handleAdd = async values => {
    try {
      await dispatch(
        createDispensesCoursBatch({
          ecoleId: ecoleid,
          anneeScolaireId,
          classeId,
          matiereId,
          sousMatiereId,
          inscriptionIds: values.inscriptionIds,
          motif: group?.motif,
        })
      ).unwrap();
      message.success('Élèves ajoutés');
      setModalVisible(false);
      form.resetFields();
      setStudentSearch('');
      loadGroup();
      dispatch(getDispensesCours({ ecoleId: ecoleid, anneeScolaireId }));
    } catch {
      message.error('Impossible d’ajouter ces élèves au groupe de dispense.');
    }
  };

  const columns = [
    { title: 'Matricule', dataIndex: 'matricule', key: 'matricule' },
    { title: 'Prénom', dataIndex: 'elevePrenom', key: 'elevePrenom' },
    { title: 'Nom', dataIndex: 'eleveNom', key: 'eleveNom' },
    { title: 'Motif historique', dataIndex: 'motif', key: 'motif', render: value => value || '-' },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record: IDispenseCours) => (
        <Popconfirm title="Retirer cet élève du groupe ?" okText="Oui" cancelText="Non" onConfirm={() => deleteDispense(record)}>
          <Button type="text" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <Card style={{ margin: 24 }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Button icon={<ArrowLeftOutlined />} onClick={goBack}>
          Retour
        </Button>
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col>
            <Title level={2}>Détail des dispenses</Title>
            {group && (
              <Space wrap>
                <Tag>{group.classeNom || 'Classe'}</Tag>
                <Tag color="blue">{formatDispenseMatiereLabel(group)}</Tag>
                <Text>Motif : {group.motifsMultiples ? 'Motifs multiples' : group.motif || '-'}</Text>
                <Text>Effectif : {group.effectif}</Text>
              </Space>
            )}
          </Col>
          <Col>
            <Button
              type="primary"
              className="matiere-add-btn"
              icon={<PlusOutlined />}
              disabled={!group || group.motifsMultiples || inscriptionOptions.length === 0}
              onClick={() => setModalVisible(true)}
            >
              Ajouter des élèves
            </Button>
          </Col>
        </Row>
        {group?.motifsMultiples && (
          <Alert
            type="warning"
            showIcon
            message="Ce groupe contient plusieurs motifs historiques. L’ajout est bloqué jusqu’au retour à un motif unique."
          />
        )}
        {!group?.motifsMultiples && inscriptionOptions.length === 0 && (
          <Alert type="info" showIcon message="Aucun autre élève éligible pour ce groupe." />
        )}
        <Input.Search
          placeholder="Rechercher par nom, prénom ou matricule"
          value={search}
          onChange={event => setSearch(event.target.value)}
          allowClear
        />
        <Table rowKey="id" loading={groupLoading} columns={columns} dataSource={filteredDispenses} />
      </Space>
      <Modal
        title="Ajouter des élèves"
        visible={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setStudentSearch('');
        }}
        onOk={() => form.submit()}
        okText="Ajouter"
        cancelText="Annuler"
        confirmLoading={updating}
        okButtonProps={{ className: 'matiere-add-btn' }}
      >
        <Form form={form} layout="vertical" onFinish={handleAdd}>
          <Form.Item
            label="Élèves inscrits"
            name="inscriptionIds"
            rules={[{ required: true, message: 'Au moins un élève est obligatoire' }]}
          >
            <Select
              mode="multiple"
              placeholder="Sélectionner des élèves"
              loading={modalLoading}
              showSearch
              searchValue={studentSearch}
              onSearch={setStudentSearch}
              filterOption={false}
            >
              {inscriptionOptions.map(option => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Motif">
            <Input.TextArea rows={3} value={group?.motif || ''} disabled />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default DispensesCoursDetail;
